<template>
  <v-modal v-model="showModel" handle-click-bg :animation="screen.breakPoint.tabPort ? 'slide-down' : 'slide-up'" fit-content :full="!screen.breakPoint.tabPort" :position="screen.breakPoint.tabPort ? 'top' : 'bottom'">
    <template v-slot:activator="{on}">
      <v-button @click="on" class="ml-auto" color="success" :width="screen.breakPoint.tabPort ? null : 'full'"><span class="d-inline-block icon-plus"></span> {{ $t('components.AddReferral.addCustomer') }}</v-button>
    </template>

    <template v-slot:header>
      {{ $t('components.AddReferral.addCustomer') }}
    </template>

    <div v-if="step === 0">
      <v-input :error="errors.name ? $t(errors.name) : null" :placeholder="$t('views.Auth.Login.name')" v-model="formData.name" class="mb-2" :disabled="loading"></v-input>
      <v-input :error="errors.lastName ? $t(errors.lastName) : null" :placeholder="$t('components.AddReferral.lastName')" v-model="formData.lastName" class="mb-2" :disabled="loading"></v-input>
      <v-input :error="errors.phoneNumber ? $t(errors.phoneNumber) : null" :placeholder="$t('views.Auth.Login.phone')" v-if="!confirmationMethodVal.EMAIL" v-model="computedPhoneNumber" class="mb-2" type-name="tel" :disabled="loading"></v-input>
      <v-input :error="errors.email ? $t(errors.email) : null" :placeholder="$t('views.Auth.Login.email')" v-if="!confirmationMethodVal.SMS" v-model="computedEmail" class="mb-2" type-name="email" :disabled="loading"></v-input>
<!--      <p class="mb-2 text-color-space text&#45;&#45;small">{{ $t('views.AddReferral.requiredIfByCall') }}</p>-->

      <div>
        <p class="text--bold mb-1">{{ $t('views.Referrals.confirmationMethod') }}</p>
        <v-radio :val="'SMS'" name="validation" v-model="confirmationMethod">{{ $t('views.Referrals.confirmationMethodSms') }}</v-radio>
        <v-radio :val="'CALL'" name="validation" v-model="confirmationMethod">
          <p>{{ $t('views.Referrals.confirmationMethodCall') }}*</p>
        </v-radio>
        <v-radio :val="'EMAIL'" name="validation" v-model="confirmationMethod">
          <p>{{ $t('views.Referrals.confirmationMethodEmail') }}*</p>
        </v-radio>
        <p class="text-color-space text--small" style="max-width: 420px;">{{ $t('views.AddReferral.requiredIfSelectedMethodCall') }}</p>
      </div>
    </div>

    <div v-else>
      <v-input
        :error="verificationCodeError ? $t(verificationCodeError) : null"
        :placeholder="codePlaceholder.full"
        :label="codePlaceholder.short"
        :class="{ errorInputClass }"
        :disabled="loading || loadingResending"
        type-name="tel"
        v-model="verificationCode"
      ></v-input>

      <div class="d-flex justify-center">
        <action-link @click="resendConfirmCode" v-if="!blockResend" size="small" class="pt-3">
          <div class="d-flex align-center">
            <spinner width="1.8" height="1.8" v-if="loadingResending"></spinner>
            <i class="icon-undo-small d-block mr-05" v-else></i>
            <span class="text--xs-small">{{ $t('views.Referrals.confirmationMethodResend') }}</span>
          </div>
        </action-link>
        <div class="pt-3" v-else>
          <span class="text-color-space">{{ $t('components.AddReferral.sendTheCodeAgainAfter') }}:</span> {{ `${this.minutes}:${this.seconds}` }}</div>
      </div>
    </div>

    <template v-slot:footer>
      <v-button
        color="success"
        width="full"
        @click="validate"
        :loading="loading"
        v-if="step === 0"
      >
        {{ $t('components.AddReferral.addCustomer') }}
      </v-button>

      <div class="d-flex" v-else>
        <v-button
          @click="step = 0"
          inline
          class="mr-auto"
          :loading="loading"
        >
          <i class="d-inline-block icon-show rotate-90"></i>
          {{ $t('components.AddReferral.backward') }}
        </v-button>

        <v-button
          color="success"
          @click="confirmReferral"
          :loading="loading"
        >
          {{ $t('components.AddReferral.registerUser') }}
        </v-button>
      </div>
    </template>
  </v-modal>
</template>

<script>
import vButton from '@/ui/controls/vButton'
import vModal from '@/ui/modals/vModal'
import ActionLink from '@/ui/controls/ActionLink'
import vInput from '@/ui/controls/vInput'
import vRadio from '@/ui/controls/vRadio'
import Spinner from '@/ui/elements/Spinner'

import screen from '@/mixins/screen'
import currContractId from '@/mixins/currContractId'
import contracts from '@/mixins/contracts'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'
import hasActiveContracts from '@/mixins/hasActiveContracts'

import {
  AddReferralGQL,
  ConfirmCodeGQL,
  ResendingConfirmCodeGQL
} from '@/graphql/mutations'
import { StatsUsersGQL } from '@/widgets/RegisteredUsers/queries'

export default {
  mixins: [screen, currContractId, contracts, notifyGraphqlError, hasActiveContracts],

  components: {
    vButton,
    vModal,
    vInput,
    vRadio,
    ActionLink,
    Spinner
  },

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  apollo: {
    statsUsers: {
      query: StatsUsersGQL,
      update: data => data.merchant.stats.statsUsers,
      skip () { return !this.hasActiveContracts }
    }
  },

  data () {
    return {
      verificationCode: null,
      verificationCodeError: null,
      errorInputClass: null,

      loading: false,
      loadingResending: false,

      formData: {
        lastName: null,
        name: null,
        phoneNumber: '',
        email: null
      },

      errors: {
        lastName: null,
        name: null,
        phoneNumber: null,
        email: null
      },

      transaction: {
        id: null
      },

      step: 0,

      timer: 60,
      blockResend: false,
      interval: undefined,
      minutes: undefined,
      seconds: undefined,

      /**
       * @values SMS, PHONE, EMAIL
       */
      confirmationMethod: 'SMS'
    }
  },

  computed: {
    confirmationMethodVal () {
      return {
        SMS: this.confirmationMethod === 'SMS',
        CALL: this.confirmationMethod === 'CALL',
        EMAIL: this.confirmationMethod === 'EMAIL'
      }
    },
    codePlaceholder () {
      const out = {
        full: this.$t('components.AddReferral.codeFromSms'),
        short: this.$t('components.AddReferral.codeFromSms')
      }
      if (this.confirmationMethodVal.EMAIL) {
        out.full = this.$t('components.AddReferral.codeFromEmail')
        out.short = this.$t('components.AddReferral.codeFromEmail')
      }
      if (this.confirmationMethodVal.CALL) {
        out.full = this.$t('views.Referrals.confirmationMethodLast4Numbers')
        out.short = this.$t('views.Referrals.confirmationMethodLast4NumbersShort')
      }

      return out
    },
    computedEmail: {
      get () {
        return this.formData.email
      },

      set (val) {
        this.formData.email = val
      }
    },

    computedPhoneNumber: {
      get () {
        return this.formData.phoneNumber
      },

      set (val) {
        this.formData.phoneNumber = val
      }
    },

    showModel: {
      get () {
        return this.value
      },

      set (val) {
        this.$emit('input', JSON.parse(val))
      }
    }
  },

  methods: {
    startTimer (duration) {
      clearInterval(this.interval)

      let timer = duration
      const timerFunc = () => {
        this.minutes = parseInt(timer / 60, 10)
        this.seconds = parseInt(timer % 60, 10)

        this.minutes = this.minutes < 10 ? '0' + this.minutes : this.minutes
        this.seconds = this.seconds < 10 ? '0' + this.seconds : this.seconds

        if (--timer < 0) {
          clearInterval(this.interval)
          this.blockResend = false
        }
      }

      timerFunc()
      this.blockResend = true
      this.interval = setInterval(timerFunc, 1000)
    },

    async addReferral () {
      try {
        this.loading = true
        const { data } = await this.$apollo.mutate({
          mutation: AddReferralGQL,
          variables: {
            params: {
              ...this.formData,
              confirmationMethod: this.confirmationMethod,
              phoneNumber: this.formData.phoneNumber.replace(/[- )(]/g, '')
            }
          }
        })

        this.transaction = data.addReferral
        this.step = 1
        this.loading = false
        this.startTimer(this.timer)
      } catch (e) {
        this.loading = false
        this.notifyGraphqlError(e)
      }
    },

    async resendConfirmCode () {
      try {
        this.loadingResending = true
        const { data } = await this.$apollo.mutate({
          mutation: ResendingConfirmCodeGQL,
          variables: {
            params: {
              transactionId: this.transaction.id,
              confirmationMethod: this.confirmationMethod
            }
          }
        })

        this.loadingResending = false
        this.transaction = data.resendingConfirmCode
        this.$notify({
          message: this.$t('components.AddReferral.confirmCodeResented')
        })
        this.startTimer(this.timer)
      } catch (e) {
        this.loadingResending = false
        this.notifyGraphqlError(e)
      }
    },

    async confirmReferral () {
      if (!this.verificationCode || this.verificationCode === '') {
        this.verificationCodeError = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        return
      }

      try {
        this.loading = true
        const { data } = await this.$apollo.mutate({
          mutation: ConfirmCodeGQL,
          variables: {
            params: {
              confirmCode: this.verificationCode,
              transactionId: this.transaction.id
            }
          }
        })

        this.transaction = data.confirmCode
        this.loading = false
        this.showModel = false
        document.dispatchEvent(new Event('addReferral'))
        await this.$apollo.queries.statsUsers.refetch()
      } catch (e) {
        this.loading = false
        setTimeout(() => {
          this.errorInputClass = 'shake'
        }, 100)
        setTimeout(() => {
          this.errorInputClass = null
        }, 1300)
        this.notifyGraphqlError(e)
      }
    },

    validate () {
      const formData = this.formData
      for (const key of Object.keys(formData)) {
        if (key === 'email' && (this.confirmationMethodVal.CALL || this.confirmationMethodVal.EMAIL) && (!formData[key] || formData[key] === '')) {
          this.errors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        } else if (key === 'phoneNumber' && !this.confirmationMethodVal.EMAIL && (!formData[key] || formData[key] === '')) {
          this.errors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        } else if (key !== 'email' && key !== 'phoneNumber' && (!formData[key] || formData[key] === '')) {
          this.errors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        } else if (key === 'phoneNumber' && formData[key] && !(/^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){10,14}(\s*)?$/).test(this.formData.phoneNumber)) {
          this.errors[key] = 'errors.Components.AddOrder.validation.mustBeAValidPhone'
        } else if (key === 'email' && formData[key] && !/^([a-zA-Z0-9_-]+\.)*[a-zA-Z0-9_-]+@[a-z0-9_-]+(\.[a-z0-9_-]+)*\.[a-z]{2,6}$/.test(formData[key])) {
          this.errors[key] = 'errors.code.validEmail'
        }
      }

      if (Object.keys(this.errors).some(key => this.errors[key] && true)) {
        return
      }

      this.addReferral()
    }
  },

  watch: {
    confirmationMethod () {
      this.errors = {
        lastName: null,
        name: null,
        phoneNumber: null,
        email: null
      }
    },

    formData: {
      handler: function () {
        this.errors = {
          lastName: null,
          name: null,
          phoneNumber: null,
          email: null
        }
      },
      deep: true
    },

    verificationCode () {
      this.verificationCodeError = null
    },

    showModel (val) {
      if (!val) {
        this.formData = {
          lastName: null,
          name: null,
          phoneNumber: null,
          email: null
        }

        this.step = 0
        this.verificationCode = null
        this.verificationCodeError = null
        this.transaction = {
          id: null
        }
      }
    }
  }
}
</script>
